import React, { useEffect, useState } from 'react';
import { Heart, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'terms' | 'privacy' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  // Close modal on Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveModal(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);
  // Lock body scroll when modal is open
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [activeModal]);
  const content = {
    terms: {
      title: 'Términos y Condiciones',
      body: <div className="space-y-4 text-warm-800" data-id="element-27">
          <p data-id="element-28">
            Bienvenido a <strong data-id="element-29">Protocolo Cuero Cabelludo en Calma</strong>. Al
            acceder y utilizar nuestros productos digitales, aceptas los
            siguientes términos y condiciones.
          </p>

          <h4 className="font-serif font-bold text-lg mt-6 mb-2" data-id="element-30">
            1. Propiedad Intelectual
          </h4>
          <p data-id="element-31">
            Todo el contenido incluido en este protocolo (textos, guías, videos,
            gráficos) es propiedad exclusiva de Protocolo Cuero Cabelludo en
            Calma y está protegido por leyes de derechos de autor. La compra te
            otorga una licencia personal e intransferible. Queda estrictamente
            prohibida su reproducción, distribución o venta a terceros.
          </p>

          <h4 className="font-serif font-bold text-lg mt-6 mb-2" data-id="element-32">
            2. Descargo de Responsabilidad Médica
          </h4>
          <p data-id="element-33">
            Este producto es de carácter educativo e informativo.{' '}
            <strong data-id="element-34">
              No sustituye el consejo, diagnóstico o tratamiento médico
              profesional.
            </strong>{' '}
            Si tienes una condición médica severa en tu cuero cabelludo,
            consulta a tu dermatólogo antes de iniciar cualquier rutina nueva.
            El uso de la información proporcionada es bajo tu propia
            responsabilidad.
          </p>

          <h4 className="font-serif font-bold text-lg mt-6 mb-2" data-id="element-35">
            3. Política de Reembolsos
          </h4>
          <p data-id="element-36">
            Debido a la naturaleza digital de nuestros productos, ofrecemos una
            garantía de satisfacción de 7 días. Si no estás satisfecho con el
            contenido, contáctanos dentro de este periodo para evaluar tu caso.
            Pasado este tiempo, no se realizarán devoluciones.
          </p>

          <h4 className="font-serif font-bold text-lg mt-6 mb-2" data-id="element-37">
            4. Modificaciones
          </h4>
          <p data-id="element-38">
            Nos reservamos el derecho de modificar estos términos en cualquier
            momento. Las actualizaciones serán efectivas inmediatamente después
            de su publicación en esta plataforma.
          </p>
        </div>
    },
    privacy: {
      title: 'Política de Privacidad',
      body: <div className="space-y-4 text-warm-800" data-id="element-39">
          <p data-id="element-40">
            En <strong data-id="element-41">Protocolo Cuero Cabelludo en Calma</strong>, nos tomamos
            muy en serio la privacidad de tus datos. Esta política describe cómo
            recopilamos y utilizamos tu información.
          </p>

          <h4 className="font-serif font-bold text-lg mt-6 mb-2" data-id="element-42">
            1. Información que Recopilamos
          </h4>
          <p data-id="element-43">
            Recopilamos información personal que nos proporcionas
            voluntariamente al realizar una compra, como tu nombre, dirección de
            correo electrónico y detalles de pago. No almacenamos información
            sensible de tarjetas de crédito directamente; utilizamos
            procesadores de pago seguros.
          </p>

          <h4 className="font-serif font-bold text-lg mt-6 mb-2" data-id="element-44">
            2. Uso de la Información
          </h4>
          <p data-id="element-45">Utilizamos tus datos para:</p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-46">
            <li data-id="element-47">
              Procesar tu pedido y entregarte el acceso al producto digital.
            </li>
            <li data-id="element-48">Enviarte actualizaciones importantes sobre el protocolo.</li>
            <li data-id="element-49">Mejorar nuestros servicios y atención al cliente.</li>
          </ul>

          <h4 className="font-serif font-bold text-lg mt-6 mb-2" data-id="element-50">
            3. Servicios de Terceros
          </h4>
          <p data-id="element-51">
            Compartimos datos estrictamente necesarios con proveedores de
            servicios de confianza (como plataformas de pago y servicios de
            email marketing) para operar nuestro negocio. Estos terceros tienen
            sus propias políticas de privacidad y seguridad.
          </p>

          <h4 className="font-serif font-bold text-lg mt-6 mb-2" data-id="element-52">
            4. Tus Derechos
          </h4>
          <p data-id="element-53">
            Tienes derecho a acceder, corregir o solicitar la eliminación de tus
            datos personales en cualquier momento. Para ejercer estos derechos,
            por favor utiliza los canales de soporte proporcionados en la
            plataforma de compra.
          </p>
        </div>
    }
  };
  return <>
      <footer className="bg-warm-900 text-cream-100 py-12 border-t border-warm-800 relative z-10" data-id="element-54">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-55">
          <div className="text-center md:text-left" data-id="element-56">
            <h4 className="font-serif text-xl mb-2" data-id="element-57">
              Protocolo Cuero Cabelludo en Calma
            </h4>
            <p className="text-warm-400 text-sm" data-id="element-58">
              Dermatología honesta y simple.
            </p>
          </div>

          <div className="flex items-center gap-2 text-sm text-warm-400" data-id="element-59">
            <span data-id="element-60">Hecho con</span>
            <Heart className="w-4 h-4 text-sage-400 fill-sage-400" data-id="element-61" />
            <span data-id="element-62">para tu bienestar</span>
          </div>

          <div className="flex gap-6 text-sm text-warm-400" data-id="element-63">
            <button onClick={() => setActiveModal('terms')} className="hover:text-cream-100 transition-colors focus:outline-none focus:text-cream-100" data-id="element-64">
              Términos
            </button>
            <button onClick={() => setActiveModal('privacy')} className="hover:text-cream-100 transition-colors focus:outline-none focus:text-cream-100" data-id="element-65">
              Privacidad
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-66">
        {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-67">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setActiveModal(null)} className="absolute inset-0 bg-warm-900/60 backdrop-blur-sm" data-id="element-68" />

            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="relative w-full max-w-2xl bg-cream-50 rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-69">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-warm-200 bg-white/50 backdrop-blur-sm sticky top-0 z-10" data-id="element-70">
                <h3 className="font-serif text-xl md:text-2xl text-warm-900 font-medium" data-id="element-71">
                  {content[activeModal].title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-2 text-warm-500 hover:text-warm-800 hover:bg-warm-100 rounded-full transition-colors" aria-label="Cerrar" data-id="element-72">
                  <X className="w-5 h-5" data-id="element-73" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 md:p-8 overflow-y-auto text-sm md:text-base leading-relaxed text-warm-700" data-id="element-74">
                {content[activeModal].body}
              </div>

              {/* Footer of modal */}
              <div className="px-6 py-4 bg-warm-50 border-t border-warm-200 flex justify-end" data-id="element-75">
                <button onClick={() => setActiveModal(null)} className="px-4 py-2 bg-sage-600 hover:bg-sage-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-76">
                  Entendido
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}